import { useEffect, useState } from 'react'
import { apiGet } from '../lib/api'
import { Badge } from './ui'

interface Source {
  policy_id: string
  policy_name: string
  effect: string
  target_type: 'resource' | 'tag_group'
  target_name?: string
}

interface Entry {
  subject_type: 'subject' | 'group'
  value: string
  decision: 'allow' | 'deny'
  sources: Source[]
}

interface EffectiveAccessPanelProps {
  tenantId: string
  resourceId: string
  enabled: boolean
}

export function EffectiveAccessPanel({ tenantId, resourceId, enabled }: EffectiveAccessPanelProps) {
  const [entries, setEntries] = useState<Entry[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!enabled || !tenantId || !resourceId) return
    setLoading(true)
    apiGet<{ items: Entry[] }>(`/api/v1/tenants/${tenantId}/resources/${resourceId}/effective-access`)
      .then((d) => setEntries(d.items ?? []))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false))
  }, [tenantId, resourceId, enabled])

  if (!enabled) return null
  if (loading) return <p className="text-xs text-muted">Loading effective access…</p>
  if (entries.length === 0) {
    return <p className="text-xs text-muted">No policy currently grants access to this resource.</p>
  }

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-foreground">Effective access</p>
      <p className="text-xs text-muted">
        Merged result of every direct and resource-group policy that applies here, resolved
        under the deny-wins rule.
      </p>
      <div className="space-y-2">
        {entries.map((e) => (
          <div key={`${e.subject_type}:${e.value}`} className="flex items-start gap-2 rounded border border-border p-2">
            <Badge variant={e.decision === 'deny' ? 'danger' : 'success'}>
              {e.decision === 'deny' ? 'Deny' : 'Allow'}
            </Badge>
            <div className="min-w-0 flex-1">
              <p className="text-sm">
                {e.subject_type === 'group' ? 'Group' : 'Subject'}: <span className="font-medium">{e.value}</span>
              </p>
              <div className="mt-1 flex flex-wrap gap-1">
                {e.sources.map((s) => (
                  <Badge key={s.policy_id} variant="default" className="text-xs">
                    {s.effect === 'deny' ? 'Deny' : 'Allow'} via {s.target_type === 'tag_group' ? (s.target_name || 'resource group') : 'this resource'} ({s.policy_name})
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
